<template>
  <div class="page-wrap">
    <!-- ── Premium Table ──────────────────────────── -->
    <PremiumTable
      title="Vehicles"
      subtitle="Manage all registered society vehicles"
      add-label="Add Vehicle"
      :show-edit="true"
      :show-delete="true"
      @add="openAdd"
      @edit="openEdit($event)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="vehicles"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Header Extra: Type Filter -->
      <template #header-extra>
        <div class="header-filters">
          <FilterDropdown
            v-model="filterType"
            :options="vehicleTypeOptions"
            item-text="label"
            item-value="value"
            placeholder="Vehicle Type"
            all-label="All Types"
            @change="onFilterChange"
          />
        </div>
      </template>

      <!-- Member Cell — name + photo only, no unit info here -->
      <template #col-member_name="{ item }">
        <div class="member-cell">
          <!-- Photo or fallback initial -->
          <img
            v-if="item.member_profile_url && item.member_profile_url !== 'null'"
            :src="API_BASE_URL + '/uploads/members/profile_pics/' + item.member_profile_url"
            :alt="item.member_name"
            class="member-avatar-img member-avatar-clickable"
            @click.stop="openMemberPhoto(item)"
          />
          <div
            v-else
            class="member-avatar"
            :style="{ background: primaryColor + '20', color: primaryColor }"
          >
            {{ item.member_name ? item.member_name.charAt(0).toUpperCase() : "M" }}
          </div>

          <div class="member-info">
            <span class="member-name">{{ item.member_name || "—" }}</span>
            <span class="member-mobile" v-if="item.member_mobile">
              <v-icon size="11" color="#94A3B8">mdi-phone-outline</v-icon>
              {{ item.member_country_code ? item.member_country_code + ' ' : '' }}{{ String(item.member_mobile) }}
            </span>
          </div>
        </div>
      </template>

      <!-- House Number Column -->
      <template #col-house_number="{ item }">
        <div class="house-number-badge" v-if="item.flat_no || item.house_number">
          <v-icon size="14" color="#94A3B8">
            {{ item.unit_type == 2 ? 'mdi-store-outline' : 'mdi-home-outline' }}
          </v-icon>
          <span v-if="item.flat_no" class="flat-prefix">{{ item.flat_no }}-</span>
          <span class="house-num">{{ item.house_number }}</span>
        </div>
        <span v-else class="dash">—</span>
      </template>

      <!-- Registration Number Cell -->
      <template #col-registration_number="{ item }">
        <span class="reg-num">{{ item.registration_number }}</span>
      </template>

      <!-- Vehicle Name Cell — type icon prefix, no separate type column -->
      <template #col-vehicle_name="{ item }">
        <div class="vehicle-name-cell">
          <v-icon size="15" class="vehicle-type-icon" :class="vehicleTypeClass(item.vehicle_type)">
            {{ vehicleTypeIcon(item.vehicle_type) }}
          </v-icon>
          <span class="vehicle-name">{{ item.vehicle_name || "—" }}</span>
        </div>
      </template>

      <!-- Parking Cell -->
      <template #col-parking_allocation="{ item }">
        <div class="parking-cell">
          <span
            class="parking-pill"
            :class="item.parking_allocation === 2 ? 'parking-reserved' : 'parking-open'"
          >
            <v-icon size="12" class="mr-1">
              {{ item.parking_allocation === 2 ? "mdi-parking" : "mdi-map-marker-outline" }}
            </v-icon>
            {{ item.parking_allocation === 2
                ? (item.parking_number ? item.parking_number : "Reserved")
                : "Open" }}
          </span>
        </div>
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ─────────────────────── -->
    <PremiumModal
      v-model="dialog"
      :title="(form.id ? 'Edit' : 'Add') + ' Vehicle'"
      :loading="saveLoading"
      submit-label="Submit"
      max-width="540"
      @submit="form.id ? updateVehicle() : saveVehicle()"
      @close="closeDialog"
    >
      <v-row dense>

        <!-- Block selector (multi-building only) -->
        <v-col v-if="useBlocks" cols="12">
          <PremiumSelect
            v-model="form.flat_id"
            label="Block"
            :items="flats"
            item-text="name"
            item-value="id"
            placeholder="Select block"
            icon="mdi-office-building-outline"
            v-validate="'required'"
            name="flat"
            data-vv-as="Block"
            :error="errors.first('flat') || serverSideErrors.flat_id"
            @change="getFlatMember(true)"
          />
        </v-col>

        <!-- Member selector -->
        <v-col cols="12">
          <PremiumSelect
            v-model="form.member_id"
            label="Member"
            :items="members"
            item-text="display_name"
            :item-value="memberSelectValue"
            placeholder="Select member"
            icon="mdi-account-outline"
            v-validate="'required'"
            name="member_id"
            data-vv-as="Member"
            :error="errors.first('member_id') || serverSideErrors.member_id"
          />
        </v-col>

        <!-- Vehicle Type -->
        <v-col cols="12" sm="6">
          <PremiumSelect
            v-model="form.vehicle_type"
            label="Vehicle Type"
            :items="vehicleTypeOptions"
            item-text="label"
            item-value="value"
            placeholder="Select Type"
            icon="mdi-car-outline"
            v-validate="'required'"
            name="vehicle_type"
            data-vv-as="Vehicle Type"
            :error="errors.first('vehicle_type') || serverSideErrors.vehicle_type"
          />
        </v-col>

        <!-- Vehicle Name -->
        <v-col cols="12" sm="6">
          <PremiumInput
            v-model="form.vehicle_name"
            label="Vehicle Name"
            placeholder="e.g. Honda Shine"
            icon="mdi-car-info"
            name="vehicle_name"
            data-vv-as="Vehicle Name"
            :error="errors.first('vehicle_name') || serverSideErrors.vehicle_name"
          />
        </v-col>

        <!-- Registration Number -->
        <v-col cols="12">
          <PremiumInput
            v-model="form.registration_number"
            label="Registration Number"
            placeholder="e.g. GJ01AB1234"
            icon="mdi-card-text-outline"
            v-validate="'required'"
            name="registration_number"
            data-vv-as="Registration Number"
            :error="errors.first('registration_number') || serverSideErrors.registration_number"
          />
        </v-col>

        <!-- Parking Allocation -->
        <v-col cols="12" sm="6">
          <PremiumSelect
            v-model="form.parking_allocation"
            label="Parking Allocation"
            :items="parkingOptions"
            item-text="label"
            item-value="value"
            placeholder="Select Parking"
            icon="mdi-parking"
            v-validate="'required'"
            name="parking_allocation"
            data-vv-as="Parking Allocation"
            :error="errors.first('parking_allocation') || serverSideErrors.parking_allocation"
          />
        </v-col>

        <!-- Parking Number (only visible when Reserved) -->
        <v-col cols="12" sm="6" v-if="form.parking_allocation === 2">
          <PremiumInput
            v-model="form.parking_number"
            label="Parking Number"
            placeholder="e.g. C-25"
            icon="mdi-numeric"
            name="parking_number"
            data-vv-as="Parking Number"
            :error="errors.first('parking_number') || serverSideErrors.parking_number"
          />
        </v-col>

      </v-row>
    </PremiumModal>

    <!-- ── Confirm Delete ──────────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Vehicle"
      message="Are you sure you want to remove this vehicle?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteVehicle"
    />

    <!-- ── Photo Viewer ───────────────────────── -->
    <MediaViewer
      v-model="photoViewer.open"
      :items="photoViewer.items"
      :index="0"
    />
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";
import { mapMembersWithDisplayName } from "@/helpers/memberDisplay";

const VEHICLE_TYPES = [
  { value: 1, label: "Two-Wheeler" },
  { value: 2, label: "Four-Wheeler" },
  { value: 3, label: "Others" },
];
const PARKING_OPTIONS = [
  { value: 1, label: "Open" },
  { value: 2, label: "Reserved" },
];

export default {
  name: "VehicleList",
  mixins: [filterMixin],
  components: {
    PremiumTable,
    PremiumModal,
    PremiumInput,
    PremiumSelect,
    FilterDropdown,
    ConfirmModal,
    MediaViewer,
  },

  data() {
    return {
      search: "",
      vehicles: [],
      totalRecords: 0,
      loading: false,
      saveLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      dialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      filterType: "",
      user: {},
      members: [],
      flats: [],
      vehicleTypeOptions: VEHICLE_TYPES,
      parkingOptions: PARKING_OPTIONS,
      isInitializing: true,
      photoViewer: { open: false, items: [] },
      form: {
        id: null,
        flat_id: "",
        member_id: "",
        unit_mapping_id: "",
        vehicle_type: "",
        vehicle_name: "",
        registration_number: "",
        parking_allocation: "",
        parking_number: "",
      },
      serverSideErrors: {},
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor", "buttonColor"]),
    ...mapGetters("society", ["useBlocks", "unitLabel", "unitType"]),

    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },

    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    societyUnitType() {
      return this.unitType;
    },

    memberSelectValue() {
      return this.members.length && this.members[0].member_identity_id
        ? "member_identity_id"
        : "id";
    },

    columns() {
      return [
        { key: "member_name", label: "Member", width: "220px" },
        { key: "house_number", label: this.unitLabel || "Unit", width: "120px" },
        { key: "vehicle_name", label: "Vehicle", width: "170px" },
        { key: "registration_number", label: "Reg. No.", width: "140px" },
        { key: "parking_allocation", label: "Parking", width: "160px", hideOnMobile: true },
      ];
    },
  },

  watch: {
    useBlocks(newVal) {
      if (!newVal) this.form.flat_id = "";
    },

    "form.parking_allocation"(val) {
      if (val !== 2) this.form.parking_number = "";
    },

    // Auto-populate unit_mapping_id whenever the selected member changes
    "form.member_id"(memberId) {
      if (!memberId) {
        this.form.unit_mapping_id = "";
        return;
      }
      const selected = this.members.find((m) => {
        if (m.member_identity_id !== undefined) {
          return String(m.member_identity_id) === String(memberId);
        }
        return String(m.id) === String(memberId);
      });
      if (selected) {
        this.form.unit_mapping_id = selected.unit_mapping_id || "";
      }
    },

    page() {
      if (this.isInitializing) return;
      this.getVehicles();
    },
    itemsPerPage() {
      if (this.isInitializing) return;
      this.page = 1;
      this.getVehicles();
    },
    search() {
      if (this.isInitializing) return;
      this.page = 1;
      this.getVehicles();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val ? String(val).trim() : "";
    },

    onFilterChange() {
      if (this.page === 1) this.getVehicles();
      else this.page = 1;
    },

    vehicleTypeLabel(type) {
      return VEHICLE_TYPES.find((t) => t.value === type)?.label || "Unknown";
    },

    vehicleTypeIcon(type) {
      if (type === 1) return "mdi-motorbike";
      if (type === 2) return "mdi-car-outline";
      return "mdi-truck-outline";
    },

    vehicleTypeClass(type) {
      if (type === 1) return "type-two";
      if (type === 2) return "type-four";
      return "type-other";
    },

    // ── Data fetching ──────────────────────────────

    getVehicles() {
      this.loading = true;
      this.$axios
        .get("societyadmin/vehicles", {
          params: {
            page: this.page,
            perPage: this.itemsPerPage,
            search: this.search ? this.search.trim() : undefined,
            vehicle_type: this.filterType || undefined,
          },
        })
        .then((res) => {
          this.loading = false;
          const data = res.data;
          if (data && data.status) {
            this.vehicles = Array.isArray(data.vehicles) ? data.vehicles : [];
            this.totalRecords = data.total || 0;
          } else {
            this.vehicles = [];
            this.totalRecords = 0;
            if (data && data.message) this.showError(data.message);
          }
        })
        .catch((err) => {
          this.loading = false;
          console.error("[Vehicles] getVehicles error:", err);
        });
    },

    getFlat() {
      return this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        } else {
          this.showError(data.message);
        }
      });
    },

    getFlatMember(resetMember = true) {
      // let url = "/societyadmin/members?society_id=" + this.user.id;
       let url = "/societyadmin/members?society_id=" + this.user.id + "&includeFamilyMember=true";
      if (this.form.flat_id === "all_shops") {
        url += "&unit_type=2";
      } else if (
        this.form.flat_id &&
        this.form.flat_id !== "null" &&
        this.form.flat_id !== ""
      ) {
        // url = "/societyadmin/flats/" + this.form.flat_id + "/members";
         url = "/societyadmin/flats/" + this.committee.flat_id + "/members?includeFamilyMember=true";
      }
      return this.$axios.get(url).then((response) => {
        const data = response.data;
        if (data.status) {
          const arr = Array.isArray(data.members)
            ? data.members
            : data.members?.data || [];
          this.members = mapMembersWithDisplayName(arr, this.societyUnitType);
          // Reset member selection when block changes (but not on initial load)
          if (resetMember) {
            this.form.member_id = "";
            this.form.unit_mapping_id = "";
          }
        } else {
          this.showError(data.message);
        }
      });
    },

    // ── Dialog open/close ──────────────────────────

    openAdd() {
      this.resetForm();
      this.$validator.reset();
      this.serverSideErrors = {};
      // If no blocks, load all members immediately (same as committee member)
      if (!this.useBlocks) this.getFlatMember(false);
      this.dialog = true;
    },

    async openEdit(item) {
      try {
        const res = await this.$axios.get("societyadmin/vehicles/" + item.id);
        const data = res.data;
        if (!data.status) {
          this.showError(data.message || "Could not load vehicle details");
          return;
        }
        const v = data.vehicle;

        // Set form values first so getFlatMember uses the correct flat_id
        this.form = {
          id: v.id,
          flat_id: v.flat_id || "",
          member_id: v.member_id ? String(v.member_id) : "",
          unit_mapping_id: v.unit_mapping_id ? String(v.unit_mapping_id) : "",
          vehicle_type: v.vehicle_type || "",
          vehicle_name: v.vehicle_name || "",
          registration_number: v.registration_number || "",
          parking_allocation: v.parking_allocation || "",
          parking_number: v.parking_number || "",
        };

        // Ensure the flats list is loaded so the Block dropdown can show the value
        if (!this.flats || this.flats.length === 0) {
          await this.getFlat();
        }

        // Load the members for this vehicle's block, then open the dialog
        // so the Member dropdown already has the full list with the saved member
        await this.getFlatMember(false);

        this.$validator.reset();
        this.serverSideErrors = {};
        this.dialog = true;
      } catch (e) {
        this.showError("Could not load vehicle details");
      }
    },

    closeDialog() {
      this.dialog = false;
      this.resetForm();
      this.$validator.reset();
      this.serverSideErrors = {};
    },

    resetForm() {
      this.form = {
        id: null,
        flat_id: "",
        member_id: "",
        unit_mapping_id: "",
        vehicle_type: "",
        vehicle_name: "",
        registration_number: "",
        parking_allocation: "",
        parking_number: "",
      };
    },

    // ── CRUD ──────────────────────────────────────

    saveVehicle() {
      this.$validator.validateAll().then((ok) => {
        if (!ok) return;
        this.saveLoading = true;
        const payload = {
          member_id: this.form.member_id,
          unit_mapping_id: this.form.unit_mapping_id || null,
          vehicle_type: this.form.vehicle_type,
          vehicle_name: this.form.vehicle_name,
          registration_number: this.form.registration_number,
          parking_allocation: this.form.parking_allocation,
          parking_number: this.form.parking_number || "",
        };
        this.$axios
          .post("societyadmin/vehicles", payload)
          .then((res) => {
            this.saveLoading = false;
            const data = res.data;
            if (data.status) {
              this.showSuccess(data.message || "Vehicle added successfully");
              this.getVehicles();
              this.closeDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => { this.saveLoading = false; });
      });
    },

    updateVehicle() {
      this.$validator.validateAll().then((ok) => {
        if (!ok) return;
        this.saveLoading = true;
        const payload = {
          member_id: this.form.member_id,
          unit_mapping_id: this.form.unit_mapping_id || null,
          vehicle_type: this.form.vehicle_type,
          vehicle_name: this.form.vehicle_name,
          registration_number: this.form.registration_number,
          parking_allocation: this.form.parking_allocation,
          parking_number: this.form.parking_number || "",
        };
        this.$axios
          .put("societyadmin/vehicles/" + this.form.id, payload)
          .then((res) => {
            this.saveLoading = false;
            const data = res.data;
            if (data.status) {
              this.showSuccess(data.message || "Vehicle updated successfully");
              this.getVehicles();
              this.closeDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => { this.saveLoading = false; });
      });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteVehicle() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/vehicles/" + this.delete_id)
        .then((res) => {
          this.deleteLoading = false;
          const data = res.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getVehicles();
            this.showSuccess(data.message || "Vehicle deleted successfully");
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => { this.deleteLoading = false; });
    },

    // ── Helpers ─────────────────────────────────

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => {
          this.serverSideErrors[e.field] = e.message;
        });
      } else {
        this.showError(data.message || "Something went wrong");
      }
    },

    showSuccess(msg) {
      this.setAlert({ showAlert: true, message: msg, variant: "green accent-4", timeout: 2000 });
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 2500, message: msg });
    },

    openMemberPhoto(item) {
      this.photoViewer.items = [{
        url: this.API_BASE_URL + '/uploads/members/profile_pics/' + item.member_profile_url,
        type: "image",
        name: item.member_name || "Member Photo",
      }];
      this.photoViewer.open = true;
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    // Restore saved filters without triggering watchers
    this.initFilters("vehicle");
    this.$nextTick(() => {
      this.isInitializing = false;
      this.getFlat();
      this.getVehicles();
    });
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Filters ── */
.header-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* ── Member Cell ── */
.member-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.member-avatar {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
}
/* Real photo avatar */
.member-avatar-img {
  width: 38px;
  height: 38px;
  min-width: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border, #e2e8f0);
}
.member-avatar-clickable {
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
}
.member-avatar-clickable:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.member-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.member-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx);
}
.member-mobile {
  font-size: 11px;
  font-weight: 500;
  color: var(--tx3, #64748b);
  display: flex;
  align-items: center;
  gap: 3px;
}

/* ── Vehicle Name with type icon ── */
.vehicle-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.vehicle-type-icon {
  flex-shrink: 0;
}
.vehicle-type-icon.type-two  { color: #059669; }
.vehicle-type-icon.type-four { color: #2563eb; }
.vehicle-type-icon.type-other { color: #7c3aed; }

/* ── Registration No ── */
.reg-num {
  font-size: 12.5px;
  font-weight: 700;
  font-family: "JetBrains Mono", monospace;
  color: var(--tx2);
  letter-spacing: 0.05em;
}

/* ── Vehicle Name ── */
.vehicle-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}

/* ── Parking Cell ── */
.parking-cell {
  display: flex;
  align-items: center;
}
.parking-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}
.parking-open {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.2);
}
.parking-reserved {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.2);
}

/* ── House Number Badge ── */
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}
.house-number-badge .house-num,
.house-number-badge .flat-prefix {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx, #1e293b);
}

/* ── Form Field Label ── */
.premium-field-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
</style>
